import {
  Content,
  TeaserVariants
} from '@ta-interaktiv/types/lib/feed-prod.unitycms.io/tenant/Content'
import { handleRequest } from './handleRequest'
import { getPropertyFromPromisedObject } from './getSingleProperty'
import { getFeedId } from './getSiteInformation'
import { MissingInfoError } from './missingInfoError'

/**
 * Construct the API Endpoint URL for article data.
 *
 * @param tenantId - The numerical ID of the tenant to query
 * @param articleId - The article ID of the desired article
 */
const apiEndpoint = (tenantId: number, articleId: number | string) =>
  `https://feed-prod.unitycms.io/${tenantId}/content/${articleId}`

const handleData = (articleId: number | string) => (data: Content) => {
  if (data === undefined || data === null) {
    throw new MissingInfoError(
      'No data could be downloaded with article ID ' + articleId
    )
  }

  return data
}

/**
 * Cache for fetch promises – this way we can repeatedly call these functions,
 * while only one fetch request goes out, reducing the strain on the APIs.
 *
 * Use `clearCache()` and `injectDataInCache()` to manipulate this cache, so
 * it is possible to change the implementation details later on.
 */
const articleDataPromises = new Map<string | number, Promise<Content>>()

/**
 * For testing purposes only – clears the cache.
 */
export function clearCache() {
  articleDataPromises.clear()
}

/**
 * For testing purposes only – injects data into the cache
 * @param key
 * @param content
 */
export function injectDataInCache(key: string | number, content: Content) {
  articleDataPromises.set(key, Promise.resolve(content))
}

/**
 * Download data for an article.
 *
 * As a side-effect, this function also stores the downloaded data on the `window.project._articles`
 * object. This is being used as a cache, in order to avoid subsequent requests.
 *
 * @param tenantId - The numerical ID of the tenant
 * @param articleId - The numerical ID of the article to download
 *
 * @returns A promise with the Content.
 *
 * @throws An error when the download fails for some reason. As opposed to the
 * site information, we can't just define default values. Instead you are
 * tasked with handling missing data on a case-by-case basis.
 */
export function getArticleInformation(
  tenantId: number,
  articleId: number | string
): Promise<Content> {
  // Check if we already downloaded the information, then get the information from there
  if (articleDataPromises.has(articleId)) {
    return articleDataPromises.get(articleId) as Promise<Content>
  }

  const promise = fetch(apiEndpoint(tenantId, articleId))
    .then(handleRequest<Content>())
    .then(handleData(articleId))

  articleDataPromises.set(articleId, promise)
  return promise
}

/**
 * Gives you the publishing date, as a ISO 8601 date string, in UTC.
 *
 * @returns An ISO 8601 date string, in UTC
 * @throws Error when download fails
 *
 * @remarks
 * This returns the date when the meta data has been created. Most of the time,
 * this should be the same as when the content has been created.
 */
export function getPublishingDate(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'published'
    )
  )
}

/**
 * Gives you the date of the latest update, as a ISO 8601 date string, in UTC.
 *
 * @returns An ISO 8601 date string, in UTC
 * @throws Error when download fails
 *
 * @remarks
 * This returns the date when the meta data has been updated. This means the
 * actual content might have been left untouched! However, for our standalones
 * it might make more sense to show the change of the teaser to signal an update?
 *
 */
export function getUpdatedDate(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta', // HACK: This returns the time of the last update of the meta data, change if this proves wrong
      'updated'
    )
  )
}

/**
 * Get the article title
 *
 * @throws Error when download fails
 */
export function getTitle(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'teaser',
      'default',
      'title'
    )
  )
}

/**
 * Get the title header (the smaller line above the title)
 *
 * @throws Error when download fails
 */
export function getTitleHeader(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'teaser',
      'default',
      'titleHeader'
    )
  )
}

/**
 * Get the lead of the article
 *
 * @throws error when the download fails
 */
export function getLead(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'teaser',
      'default',
      'lead'
    )
  )
}

/**
 * Get the doc type, which is used by the paywall (as `doc_type`) and GTM
 * (as `pagetype`).
 *
 * @throws error when the download fails
 */
export function getDocType(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'paywall',
      'docType'
    )
  )
}

/**
 * Loads whether the article is Abo+ or not
 *
 * @throws error when the download fails
 */
export function isPremium(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'settings',
      'premium'
    )
  )
}

/**
 * Returns the URL path for a category. Split by '/', and you should be able to
 * get the the main category that is being used by GTM and the paywall.
 *
 * | Category | Property for GTM | Property for Paywall |
 * | -------- | ---------------- | -------------------- |
 * | array[0] | category         | main_channel         |
 * | array[1] | subcategory      | sub_channel          |
 *
 * @throws error when the downlad fails
 */
export function getFullCategoryPath(
  domain: string,
  articleId: number | string
) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'mainCategoryFullUrlPath'
    )
  )
}

/**
 * Return the main category / main_channel
 *
 * @throws error when the download fails
 */
export function getCategory(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'mainCategoryFullUrlPath'
    ).then(path => path?.split('/')[0])
  )
}

/**
 * Return the subcategory / sub_channel
 *
 * @throws when the download fails
 */
export function getSubCategory(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'mainCategoryFullUrlPath'
    ).then(path => {
      const splitPath = path?.split('/')
      return splitPath && splitPath[1] ? splitPath[1] : path
    })
  )
}

/**
 * Return the authors as a list
 *
 * @throws when the download fails
 */
export function getAuthors(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'authors'
    ).then(authorList => {
      return authorList?.map((authorObject: any) => authorObject.name) ?? []
    })
  )
}

/**
 * Return the comment count. Returns 0 in case the clientData property is undefined.
 *
 * @throws when the download fails
 */
export function getCommentCounts(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'clientData',
      'community',
      'comments'
    ).then(commentCount => (commentCount ? parseInt(commentCount, 10) : 0))
  )
}

/**
 * Returns the URL to a teaser image.
 * @param domain
 * @param articleId
 * @param teaserSize
 */
export function getTeaserImgSrc(
  domain: string,
  articleId: number | string,
  teaserSize: keyof TeaserVariants = '800px'
) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'teaser',
      'default',
      'image',
      'variants',
      teaserSize,
      'src'
    )
  )
}

export function getUrl(domain: string, articleId: number | string) {
  return getFeedId(domain).then(id =>
    getPropertyFromPromisedObject(
      getArticleInformation(id as number, articleId),
      'content',
      'meta',
      'teaser',
      'default',
      'url'
    ).then(url => {
      if (!url) {
        return undefined
      }

      // Check if the protocol is already part of the URL
      if (/^https?:/.test(url)) {
        // Return the URL as-is
        // ASSUMPTION: The URL for each tenant is correctly set in Unity
        return url
      }

      // Otherwise prepend the homepage
      return `https://www.${domain}.ch${url}`
    })
  )
}
